import React, { useState, useCallback } from 'react';
import { FaHeart, FaSlidersH, FaShareAlt, FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';
import { getFirebase } from '@services/firebase';
import ProfileUsagePopup from './ProfileUsagePopup';

let db;

const getDb = () => {
  if (!db) {
    const { db: firebaseDb } = getFirebase();
    db = firebaseDb;
  }
  return db;
};

const styles = `
  .action-btn {
    color: #333 !important;
    background: rgba(255, 255, 255, 0.6) !important;
    border: none !important;
    border-radius: 6px;
    padding: 0 !important;
    margin: 0 !important;
    width: 28px !important;
    height: 28px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    flex-shrink: 0 !important;
  }

  .action-btn:not(.action-btn--active):disabled {
    color: #333 !important;
    cursor: not-allowed !important;
  }

  .action-btn.action-btn--active,
  .action-btn.action-btn--active:disabled {
    color: #fa4848 !important;
    border: none !important;
    box-shadow: none !important;
  }


  .unified-profile-card .action-btn,
  .unified-profile-card .action-btn:disabled {
    color: #333 !important;
    background: rgba(255, 255, 255, 0.6) !important;
    border: none !important;
  }

  .unified-profile-card .action-btn--active,
  .unified-profile-card .action-btn--active:disabled {
    color: #fa4848 !important;
  }

  html[data-theme="dark"] .action-btn {
    color: #ececec !important;
    background: rgba(0, 0, 0, 0.5) !important;
    border: none !important;
  }

  html[data-theme="dark"] .action-btn--active,
  html[data-theme="dark"] .action-btn--active:disabled {
    color: #fa4848 !important;
    border: none !important;
    box-shadow: none !important;
  }

  html[data-theme="dark"] .unified-profile-card .action-btn {
    color: #ececec !important;
    background: rgba(0, 0, 0, 0.5) !important;
    border: 1px solid #fa4848 !important;
  }

  html[data-theme="dark"] .unified-profile-card .action-btn--active {
    color: #fa4848 !important;
  }

  .profile-card {
    margin-left: 10px !important;
    margin-right: 10px !important;
    padding: 10px !important;
    box-sizing: border-box !important;
  }

  .profile-card h3 {
    font-size: clamp(12px, 2vw, 16px);
    line-height: 1.5em;
    margin-bottom: 5px !important;
  }

  .profile-card p {
    font-size: clamp(14px, 2vw, 18px);
    line-height: 1.3em;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    .profile-card {
      margin-left: 8px !important;
      margin-right: 8px !important;
    }
    .profile-card h3 {
      font-size: clamp(14px, 1.5vw, 16px);
    }
    .profile-card p {
      font-size: clamp(14px, 1.5vw, 16px);
    }
  }

  @media (max-width: 600px) {
    .profile-card {
      margin-left: 5px !important;
      margin-right: 5px !important;
      aspect-ratio: 1 / 1.1 !important;
    }
    .profile-card h3 {
      font-size: clamp(10px, 1.5vw, 12px);
      white-space: normal;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .profile-card p {
      display: none !important;
    }
    .image-container {
      width: 100% !important;
      aspect-ratio: 1 / 1 !important;
    }
  }
`;

const HomeProfileCard = ({
  theme,
  flavour,
  filling,
  cover,
  image,
  showCustomIcon = false,
  showFavouriteIcon = false,
  showShareAction = false,
  showVisibilityIcon = false,
  showDeleteIcon = false,
  isInCustom = false,
  isInFavourites = false,
  isVisible = true,
  onAddToCustom,
  onAddToFavourites,
  onShare,
  onToggleVisibility,
  onDelete,
  onClick,
  userRole,
  profileId,
}) => {
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    if ((userRole === 'Admin' || userRole === 'Super') && !isVisible) {
      setShowPopup(true);
    } else {
      onDelete();
    }
  }, [userRole, isVisible, onDelete]);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleToggleVisibility = async (e) => {
    e.stopPropagation();
    setIsTogglingVisibility(true);
    try {
      await onToggleVisibility();
    } catch (err) {
      console.error('Error in handleToggleVisibility:', err.message, err.stack);
      setError('Failed to toggle visibility');
    } finally {
      setIsTogglingVisibility(false);
    }
  };

  const formatDescription = () => {
    const flavourText = (flavour || 'unknown').toLowerCase();
    const fillingText = (filling || 'none').toLowerCase();
    const coverText = (cover || 'none').toLowerCase();
    const description = `${flavourText} flavour with ${fillingText} filling, covered by ${coverText}.`;
    return description.charAt(0).toUpperCase() + description.slice(1);
  };

  // Prevent clicks from popup or confirm dialog from triggering card click
  const handleClick = (e) => {
    if (e.target.closest('.popup-overlay') || e.target.closest('.confirm-dialog')) {
      return;
    }
    onClick();
  };

  return (
    <div
      className="profile-card"
      onClick={handleClick}
      style={{
        minHeight: '150px',
        cursor: 'pointer',
      }}
    >
      <style>{styles}</style>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          className="image-container"
          style={{
            position: 'relative',
            width: '80%',
            alignSelf: 'center',
            aspectRatio: '4 / 3',
            overflow: 'hidden',
            borderRadius: '5px',
            flexShrink: 0,
            backgroundColor: 'var(--bg-section)',
          }}
        >
          {image ? (
            <img
              src={image}
              alt={flavour || 'Profile'}
              className="card-image"
              style={{
                width: '100%',
                height: '100%',
                minWidth: '100%',
                minHeight: '100%',
                objectFit: 'fill',
                objectPosition: 'center',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                fontSize: 'clamp(10px, 2vw, 14px)',
              }}
            >
              No Image
            </div>
          )}

          {(showCustomIcon || showFavouriteIcon || showShareAction || showVisibilityIcon || showDeleteIcon) && (
            <div
              className="card-actions"
              style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 10,
                padding: '0',
                background: 'transparent',
              }}
            >
              <div className="card-actions-left" style={{ display: 'flex', gap: '5px' }}>
                {showShareAction && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShare();
                    }}
                    className="action-btn"
                    title="Share Profile"
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaShareAlt />
                  </button>
                )}
                {showCustomIcon && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToCustom();
                    }}
                    className={`action-btn${isInCustom ? ' action-btn--active' : ''}`}
                    disabled={isInCustom}
                    title={isInCustom ? 'Already in Custom' : 'Add to Custom'}
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: isInCustom ? 'not-allowed' : 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaSlidersH />
                  </button>
                )}
                {showVisibilityIcon && !isVisible && (
                  <button
                    onClick={handleToggleVisibility}
                    className="action-btn"
                    title="Make Visible"
                    disabled={isTogglingVisibility}
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: isTogglingVisibility ? 'not-allowed' : 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaEye />
                  </button>
                )}
              </div>
              <div className="card-actions-right" style={{ display: 'flex', gap: '5px' }}>
                {showFavouriteIcon && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToFavourites();
                    }}
                    className={`action-btn${isInFavourites ? ' action-btn--active' : ''}`}
                    disabled={isInFavourites}
                    title={isInFavourites ? 'Already in Favourites' : 'Add to Favourites'}
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: isInFavourites ? 'not-allowed' : 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaHeart />
                  </button>
                )}
                {showVisibilityIcon && isVisible && (
                  <button
                    onClick={handleToggleVisibility}
                    className="action-btn"
                    title="Make Invisible"
                    disabled={isTogglingVisibility}
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: isTogglingVisibility ? 'not-allowed' : 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaEyeSlash />
                  </button>
                )}
                {showDeleteIcon && (
                  <button
                    onClick={handleDeleteClick}
                    className="action-btn delete-btn"
                    title="Delete Profile"
                    style={{
                      fontSize: '16px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textShadow: '0 0 4px rgba(255, 255, 255, 0.9)',
                    }}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', margin: '2px 5px 0 5px' }}>
          <h3
            style={{
              textAlign: 'center',
              margin: '4px 2px 1px 2px',
              color: 'var(--text-primary)',
            }}
          >
            {theme?.trim() ? theme : flavour?.trim() || 'Untitled'}
          </h3>
          <p
            style={{
              color: 'var(--text-secondary)',
              margin: '4px 1px 0 1px',
              textAlign: 'justify',
              padding: '0 0 0 0',
              lineHeight: '1.2em',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 6,
            }}
          >
            {formatDescription()}
          </p>
          {error && (
            <p
              className="error"
              style={{
                color: 'red',
                margin: '1px 0 0 0',
                textAlign: 'justify',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {error}
            </p>
          )}
        </div>
      </div>

      {showPopup && (
        <ProfileUsagePopup
          profileId={profileId}
          onClose={handleClosePopup}
          onDeleteProfile={onDelete}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default React.memo(HomeProfileCard);